import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, HostListener, Input } from '@angular/core';
import { OpenAndClosedRevenuesService } from './open-and-closed-revenues.service';
import { ColorHelper } from '@swimlane/ngx-charts';
import { MatterService } from 'src/app/pages/dashboards/matters/matter.service';
import { UserProfileService } from 'src/app/core/services/user.service';
import { NgbCalendar, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-open-and-closed-revenues',
    templateUrl: './open-and-closed-revenues.component.html',
    styleUrls: ['./open-and-closed-revenues.component.scss'],
    standalone: false
})
export class OpenAndClosedRevenuesComponent implements OnInit, AfterViewInit {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;

  // Input property to control filter visibility from parent component
  @Input() isFilterVisible: boolean = false;

  // Chart options for number cards
  view: any[] = [700, 400];
  animations: boolean = true;
  colorScheme = {
    domain: ['#5AA454', '#E44D25', '#CFC0BB', '#7aa3e5', '#a8385d', '#aae3f5']
  };
  
  // Chart data
  data: any[] = [];
  originalData: any[] = [];
  
  // Chart options that might be referenced elsewhere in code
  showDataLabel: boolean = false;
  tooltipDisabled: boolean = false;
  gradient: boolean = false;
  showXAxis: boolean = true;
  showYAxis: boolean = true;
  showXAxisLabel: boolean = true;
  showYAxisLabel: boolean = true;
  xAxisLabel: string = 'Revenue';
  yAxisLabel: string = 'Category';
  
  // Filter options
  organizationOptions: any[] = [];
  matterOptions: any[] = [];
  practiceAreaOptions: any[] = [];
  userOptions: any[] = [];
  
  // Selected filter values
  selectedDateRange: any = null;
  dateRangeValue: Date[] = [];
  minDateValue: Date = new Date(2020, 0, 1);
  maxDateValue: Date = new Date(2050, 11, 31); // Allow dates far into the future
  selectedOrganization: any;
  
  // Filter visibility - always true as it's controlled by parent through isFilterVisible
  showFilters: boolean = true;
  selectedMatter: any;
  selectedPracticeArea: any;
  selectedUser: any;
  
  // Old filters
  selectedFilter: any;
  filterOptions: any[] = [];
  
  // Color helper for legend
  colors: ColorHelper | undefined;
  
  // Loading state
  isLoading: boolean = false;

  constructor(
    private revenuesService: OpenAndClosedRevenuesService,
    private matterService: MatterService,
    private userService: UserProfileService,
    private calendar: NgbCalendar,
    private modalService: NgbModal
  ) {}

    ngOnInit(): void {
    // Initialize default date range (today-30 to today)
    const today = new Date();
    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 30);
    
    // Set default date range for p-calendar
    this.dateRangeValue = [defaultStartDate, today];
    
    this.selectedDateRange = {
      start: defaultStartDate,
      end: today
    };
    
    this.fetchFilterOptions();
    this.fetchRevenueData();
    // Set showDataLabel to false initially - we'll show values in the tooltip
    this.showDataLabel = false;
  }
  
  ngAfterViewInit(): void {
    // Initialize chart size after view is initialized
    setTimeout(() => {
      this.updateChartSize();
    }, 100);
  }

  fetchFilterOptions(): void {
    this.isLoading = true;
    
    // Use the new organizations API endpoint
    this.revenuesService.getOrganizationNames()
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.organizationOptions = response.data;
          }
        },
        error: (error: any) => {
          console.error('Error fetching organizations:', error);
          this.isLoading = false;
        }
      });
    
    // Only fetch matters if organization is selected
    if (this.selectedOrganization) {
      this.fetchMattersByOrganization(this.selectedOrganization);
    } else {
      // Clear matters if no organization is selected
      this.matterOptions = [];
      this.selectedMatter = null;
    }
    
    // Get practice areas
    this.revenuesService.getPracticeAreas()
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.practiceAreaOptions = response.data.map((area: any) => ({
              id: area.description,
              name: area.description
            }));
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error('Error fetching practice areas:', error);
          this.isLoading = false;
        }
      });
    
    // Get users
    this.userService.getUserNamesAndEmails()
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.userOptions = response.data.map((user: any) => ({
              id: user.email,
              name: user.fullName
            }));
          }
        },
        error: (error: any) => {
          console.error('Error fetching users:', error);
        }
      });
  }

  fetchRevenueData(): void {
    this.isLoading = true;
    
    // Create payload according to the required structure
    const payload: any = {
      startDate: this.selectedDateRange && this.selectedDateRange.start ? 
        this.formatDate(this.selectedDateRange.start) : '',
      endDate: this.selectedDateRange && this.selectedDateRange.end ? 
        this.formatDate(this.selectedDateRange.end) : '',
      organization: this.selectedOrganization || '',
      matter: this.selectedMatter?.name || this.selectedMatter || '',
      practiceArea: this.selectedPracticeArea || '',
      createdBy: this.selectedUser || '',
      projectName: '',
      projectTypeCode: ''
    };
    
    this.revenuesService.getRevenueData(payload).subscribe(
      (response: any) => {
        this.isLoading = false;
        if (response && response.data) {
          this.processData(response.data);
          this.updateChartSize();
        }
      },
      (error: any) => {
        this.isLoading = false;
        console.error('Error fetching revenue data:', error);
      }
    );
  }

  processData(data: any[]): void {
    this.originalData = data.map(item => ({
      name: item.taskCategory,
      value: item.revenue,
      hours: item.taskHours,
      extra: {
        hours: item.taskHours
      }
    }));

    // Sort data by revenue value in descending order
    this.originalData.sort((a, b) => b.value - a.value);
    
    // Initialize chart data - limit to top 6 cards for better display
    this.data = this.originalData.slice(0, 6);
    
    // Create filter options for the old category filter
    this.generateFilterOptions();
  }

  generateFilterOptions(): void {
    this.filterOptions = [...new Set(this.originalData.map(item => item.name))].map(name => {
      return { label: name, value: name };
    });
  }
  
  formatDate(date: Date): string {
    return date ? `${this.padNumber(date.getMonth() + 1)}/${this.padNumber(date.getDate())}/${date.getFullYear()}` : '';
  }
  
  formatDateRangeDisplay(): string {
    if (!this.dateRangeValue || this.dateRangeValue.length < 2) {
      return 'Select Range';
    }
    
    const start = this.dateRangeValue[0];
    const end = this.dateRangeValue[1];
    
    // Format as MM/DD/YY
    const startStr = `${start.getMonth() + 1}/${start.getDate()}/${start.getFullYear().toString().substr(2)}`;
    const endStr = `${end.getMonth() + 1}/${end.getDate()}/${end.getFullYear().toString().substr(2)}`;
    
    return `${startStr} - ${endStr}`;
  }
  
  padNumber(value: number): string {
    return value < 10 ? `0${value}` : value.toString();
  }
  
  onDateSelect(event: any): void {
    // PrimeNG calendar will handle the date range selection
    if (this.dateRangeValue && this.dateRangeValue.length === 2) {
      const [startDate, endDate] = this.dateRangeValue;
      
      // Update the selected date range
      this.selectedDateRange = {
        start: startDate,
        end: endDate
      };
    }
  }
  
  applyAllFilters(): void {
    // Update date range if selected
    if (this.dateRangeValue && this.dateRangeValue.length === 2) {
      const [startDate, endDate] = this.dateRangeValue;
      
      this.selectedDateRange = {
        start: startDate,
        end: endDate
      };
    }
    
    // Fetch revenue data with all current filters
    this.fetchRevenueData();
  }

  @HostListener('window:resize')
  updateChartSize(): void {
    if (this.chartContainer) {
      const containerWidth = this.chartContainer.nativeElement.offsetWidth;
      
      // Card-based layout: height based on number of items
      // Each card needs ~80-100px height
      const itemCount = this.data.length || 1;
      const heightPerItem = 90;
      const calculatedHeight = itemCount * heightPerItem;
      
      // Compact but readable
      const chartHeight = Math.max(280, Math.min(calculatedHeight, 450));
      
      this.view = [containerWidth * 0.95, chartHeight];
    }
  }

  // Old category filter
  onSelectFilter(event: any): void {
    if (event) {
      this.data = this.originalData.filter(item => item.name === event.value);
    } else {
      this.data = [...this.originalData.slice(0, 6)];
    }
    
    this.updateChartSize();
  }

  // New filter handlers
  // Date range change is now handled by applyDateFilter
  
  // Filter change handlers now just update the selection without fetching data
  onOrganizationChange(event: any): void {
    console.log('Organization changed:', event);
    
    // Reset matter selection when organization changes
    this.selectedMatter = null;
    
    // Fetch matters for the selected organization
    if (this.selectedOrganization) {
      this.fetchMattersByOrganization(this.selectedOrganization);
    } else {
      // Clear matters if no organization is selected
      this.matterOptions = [];
    }
  }
  
  /**
   * Fetch matters based on selected organization
   */
  fetchMattersByOrganization(organization: string): void {
    this.isLoading = true;
    this.revenuesService.getMatterNames(organization)
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.matterOptions = response.data;
            console.log(`Matters loaded for ${organization}:`, this.matterOptions);
          } else {
            this.matterOptions = [];
            console.warn(`No matters found for ${organization}`);
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error(`Error loading matters for ${organization}:`, error);
          this.matterOptions = [];
          this.isLoading = false;
        }
      });
  }
  
  onMatterChange(event: any): void {
    // Update matter selection only
  }
  
  onPracticeAreaChange(event: any): void {
    // Update practice area selection only
  }
  
  onUserChange(event: any): void {
    // Update user selection only
  }
  
  // Clear all filters
  clearAllFilters(): void {
    // Reset date range to default (today-30 to today)
    const today = new Date();
    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 30);
    
    // Update the calendar component
    this.dateRangeValue = [defaultStartDate, today];
    
    this.selectedDateRange = {
      start: defaultStartDate,
      end: today
    };
    
    this.selectedOrganization = null;
    this.selectedMatter = null;
    this.selectedPracticeArea = null;
    this.selectedUser = null;
    this.fetchRevenueData();
  }
  
  /**
   * Toggle filter visibility
   * Note: This method is now controlled by the parent component through isFilterVisible
   */
  toggleFilters(): void {
    // This method is now deprecated as filter visibility is controlled by the parent
    // through the isFilterVisible input property
    this.showFilters = true; // Always true as visibility is controlled by isFilterVisible
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  formatTooltip(data: any): string {
    if (!data) return '';
    
    const item = this.originalData.find(d => d.name === data.name) || data;
    
    return `<div class="tooltip-container">
      <div class="tooltip-title">${data.name}</div>
      <div class="tooltip-value">Revenue: ${this.formatCurrency(data.value)}</div>
      <div class="tooltip-hours">Hours: ${item.hours || 'N/A'}</div>
    </div>`;
  }

  isInactive(item: any): boolean {
    return false; // Since we removed legends, no items are inactive
  }
}
